/**
 * The base content, and therefore the schema.
 *
 * `Dictionary` in `src/i18n/dictionary.ts` is `typeof en`, so this file is the
 * only place a string is *introduced*. Every other locale is a partial of this
 * shape and inherits whatever it has not said yet.
 *
 * The five written pages live in `content/pages/` and are composed in here rather
 * than inlined: each is 100–170 lines of copy, and one file holding all of them
 * would be unreadable. They are still part of this dictionary, so the translation
 * model is unchanged — a locale that translates a page supplies its whole
 * `sections` array, which is what the atomic-array rule in `Translation` already
 * says about arrays.
 *
 * The copy is Halil Aksu's, August 2026, drafted from the Digitopia Corporate
 * Presentation 2026 v3. What was changed on the way in is recorded in
 * `docs/website/COPY-REVIEW.md`.
 */
// `home` is the one page imported by name: `pages/NotFound.tsx` reads
// `t.pages.home.navLabel`, and naming it below keeps that access precisely
// typed. Every other page arrives through the glob.
import { CONTACT_FALLBACK_EMAIL } from '../lib/contact'
import { home } from './pages/home'
import { pagesFrom } from './pageRegistry'
import type { WrittenPage } from './sections'
import { toolsUi } from './tools/ui'
import { gutsCheck } from './tools/guts-check'
import { diagnostic } from './tools/diagnostic'
import { ari } from './tools/ari'
import { roiCalculator } from './tools/roi-calculator'
import { LINKS } from './pages/links'
import { BAND_FORM_EN } from './bandForm'

/**
 * Every page module in `content/pages/`, keyed by its export name — which is
 * what `t.pages[route.id]` in `App.tsx` and `lib/seo.ts` looks up. Adding a page
 * is adding a file; there is no list to append to and therefore nothing for two
 * concurrent runs to collide on. See `content/pageRegistry.ts`.
 *
 * The type is deliberately an intersection rather than a plain `Record`. Naming
 * `home` in it keeps `t.pages.home` precise for `pages/NotFound.tsx`, which
 * reads it that way; without it `noUncheckedIndexedAccess` would make that
 * access possibly-undefined. And spreading the glob into a bare object literal
 * dropped the index signature altogether, which collapsed
 * `keyof Dictionary['pages']` to `'home'` and rejected all fourteen literal ids
 * in `app/routes.ts`.
 *
 * **What this does NOT give back is the missing-page check.** Measured, not
 * assumed: with this type, `keyof Dictionary['pages']` is `string` and
 * `dict.pages[someId]` is `WrittenPage`, not `WrittenPage | undefined` — so an
 * id with no page is still a blank page that prerenders happily rather than a
 * type error. `scripts/check-routes.mjs`, the first step of `pnpm build`, is
 * what catches it now. See the note on `RouteDef.id` in `app/routes.ts`.
 */
const PAGES_ALL: Record<string, WrittenPage> & { home: WrittenPage } = {
  ...pagesFrom(import.meta.glob<Record<string, unknown>>('./pages/*.ts', { eager: true })),
  home,
}

// No `as const`: it would widen nothing and narrow everything — every string
// would become its own literal type, and a translation of "Home" would have to
// be the string "Home". Inferred `string` is what makes the locale files
// assignable.
export const en = {
  site: {
    name: 'Digitopia',
    /** Appended after the page title: "About Digitopia · Digitopia". */
    titleSuffix: 'Digitopia',
    tagline: 'Discover. Deploy. Outcome.',
    description:
      'Strategy consulting, transformation management and enterprise AI solutions. Digitopia deploys agentic AI so your business gets an outcome, not a pilot.',
  },

  nav: {
    languageLabel: 'Language',
    skipToContent: 'Skip to content',
    /** The hamburger button in the header below `sm`, where the nav bar cannot fit. */
    menu: 'Menu',
    /** The live site puts a plain text link to the platform beside the action. */
    login: 'Impact Platform',
    loginHref: 'https://impact.digitopia.ai',
    cta: {
      label: 'Contact Us',
      href: LINKS.contact,
    },
    /**
     * The header nav, as specified: Solutions · Customers · Partners · Resources ·
     * About Us, with `Platform` removed (the platforms consolidate under
     * impact.digitopia.ai).
     *
     * Written out rather than derived from the route registry, because three of
     * the five point at imported legacy pages. The nav is a curated list; deriving
     * it from "pages we designed" would silently drop Customers and Resources.
     *
     * **Each item carries the panel that opens under it on hover** — `blurb` is
     * the left column, `menu` the list beside it, the way digitopia.co's own
     * header behaves. The destinations are deliberately the same five sets the
     * footer lists: a reader should not find a page in the footer that the header
     * hides, and those hrefs are already the ones the link checker walks.
     */
    items: [
      {
        label: 'Solutions',
        href: '/en/solutions',
        blurb: 'An outcome focused approach.',
        menu: [
          {
            label: 'Strategy Consulting',
            href: LINKS.strategy,
            description: 'Strategize and prioritize your agentic AI journey.',
          },
          {
            label: 'Transformation Management',
            href: LINKS.transformation,
            description: 'Assure and accelerate your agentic AI journey.',
          },
          {
            label: 'Enterprise AI Solutions',
            href: LINKS.enterpriseAi,
            description: 'Deploy your agentic AI solutions at scale.',
          },
        ],
      },
      {
        label: 'Customers',
        href: LINKS.successStories,
        blurb: 'Who we work with, and what changed after we did.',
        menu: [
          {
            label: 'Hall of Fame',
            href: LINKS.hallOfFame,
            description: 'The companies leading on digital and AI maturity.',
          },
          {
            label: 'Success Stories',
            href: LINKS.successStories,
            description: 'What changed, told the way they tell it.',
          },
        ],
      },
      {
        label: 'Partners',
        href: '/en/partner-program',
        blurb: 'Deliver transformation with us, and to your own customers.',
        menu: [
          {
            label: 'Partner Program',
            href: '/en/partner-program',
            description: 'Take our frameworks to your own market.',
          },
        ],
      },
      {
        label: 'Resources',
        href: '/en/impact-labs',
        blurb: 'Impact Labs — our thinking on digital and AI transformation.',
        menu: [
          /**
           * First in the list because it is the only item a reader can *do*
           * something with — everything below it is reading material. The footer
           * already carries a Tools column; this is the header's way in (asked
           * for 2026-08-28).
           */
          {
            label: 'Tools',
            href: '/en/tools',
            description: 'Four short tools, each ending in a result you can act on.',
          },
          { label: 'Blog', href: '/en/blog', description: 'Weekly writing from our practitioners.' },
          {
            label: 'Benchmark',
            href: '/en/impact-labs/benchmark',
            description: 'Digital and AI maturity by sector, country and size.',
          },
          {
            label: 'Reports',
            href: LINKS.reports,
            description: 'Benchmark research and maturity findings.',
          },
          {
            label: 'Ebooks',
            href: '/en/impact-labs/ebooks',
            description: 'Longer guides, free to download.',
          },
          {
            label: 'Podcasts',
            href: '/en/impact-labs/podcasts',
            description: 'Conversations with transformation leaders.',
          },
        ],
      },
      {
        label: 'About Us',
        href: LINKS.about,
        blurb: 'Why we exist, who does the work, and how to reach us.',
        menu: [
          { label: 'About Us', href: LINKS.about, description: 'Why we exist and how we work.' },
          {
            label: 'News',
            href: '/en/impact-labs/news',
            description: 'What Digitopia is announcing.',
          },
          {
            label: 'Events',
            href: '/en/impact-labs/events',
            description: 'Where to meet us next.',
          },
          { label: 'Careers', href: LINKS.careers, description: 'Become a Digitopian.' },
          {
            label: 'Contact Us',
            href: LINKS.contact,
            description: 'Start a conversation with our team.',
          },
        ],
      },
    ],
  },

  pages: PAGES_ALL,

  /**
   * The assessment tools' own copy — the chrome they share, the lead gate, and
   * each tool's questions.
   *
   * Inside the dictionary rather than beside the components, so a locale can
   * translate twelve questions and the compiler names the ones it has not. The
   * arrays are atomic like every other array here: a locale supplies a whole
   * question list or inherits English's.
   */
  tools: {
    ...toolsUi,
    gutsCheck,
    diagnostic,
    ari,
    roiCalculator,
  },

  /**
   * The closing call to action above the footer on every document, imported
   * pages included (asked for 2026-08-26: "her sayfanın altında olacak contact
   * us kısmı").
   *
   * **It is the form itself as of 2026-08-30**, not a card with a button back to
   * Contact Us — "sayfa sonlarındaki contact us tasarımımız … onlar yerine artık
   * direkt form koyuyoruz". `sub` is the line under the heading; the fields live
   * in `content/bandForm.ts` so English and Turkish can differ in their Country
   * and Industry lists without this file carrying 250 country names twice.
   *
   * `cta` and `photoAlt` are kept because five locales still supply them and
   * removing the keys would be five edits for nothing. Nothing renders them
   * today — the gradient card and its cut-out are gone.
   */
  contactBand: {
    heading: "Let's Get in Touch!",
    sub: 'Tell us where you are heading and we will bring the shortest route.',
    form: BAND_FORM_EN,
    cta: { label: 'Contact Us', href: LINKS.contact },
    /** The cut-out supplied with the reference; alt text, not decoration. */
    photoAlt: 'Brightly coloured cacti framed by a Digitopia gradient ribbon',
  },

  footer: {
    /**
     * The newsletter box. Rendered in the footer of all 248 documents, wired by
     * the script in `index.html` — see the comment beside it in `Layout.tsx` for
     * why the behaviour cannot live in React.
     *
     * These strings are handed to that script as `data-` attributes, because it
     * runs outside the app and has no access to the dictionary. They are the
     * one set of user-facing words on this site that a component does not read.
     */
    newsletter: {
      heading: 'The monthly read',
      sub: 'One email on the second Tuesday of the month: what we are seeing in digital and AI transformation, and what we would do about it.',
      placeholder: 'you@company.com',
      submit: 'Subscribe',
      /**
       * Said whether the address was new, already pending, or already
       * subscribed. The endpoint answers identically in all three cases on
       * purpose — a box that distinguishes them tells a stranger who is on our
       * list. See `request_newsletter`.
       */
      sent: 'Check your email — we have sent you a link to confirm.',
      invalid: 'That email address does not look right.',
      failed: 'Something went wrong at our end. Please try again in a moment.',
      /**
       * Shown when the bot check has not produced its token yet. Fatih hit this
       * on the day the box shipped and was told "something went wrong at our
       * end", which was both untrue and unactionable — the form now waits for
       * the check and says so.
       */
      checking: 'Just finishing the check that proves you are human…',
      /**
       * The check errored, never loaded, or produced nothing in 8 s. The
       * Worker refuses a signup with no token, so "finishing the check…" would
       * sit there forever (2026-10-02).
       */
      blocked: `The security check did not load in your browser. Reload the page or turn off your ad blocker for this site — or email us at ${CONTACT_FALLBACK_EMAIL}.`,
      /** Under the button, before anyone types. Double opt-in, said plainly. */
      note: 'We send a confirmation link first, and every issue has a one-click unsubscribe.',
    },
    tagline: 'Discover. Deploy. Outcome.',
    signOff: '#ScaleValue',
    /** Rendered as "© 2026 Digitopia. <rights>" — the year is computed. */
    rights: 'All rights reserved.',
    /**
     * The Tools column is BACK, as of 2026-08-25, and complete.
     *
     * It was deleted with a note saying it would return when the tools existed:
     * the document listed three, and two of them had no page. Kagan supplied
     * three built tools that day and all three are now ported, so the column
     * carries the hub and each of them.
     *
     * The ROI Calculator — the fourth item on that original document, and the
     * one this comment used to say was neither built nor the same thing as the
     * other three — arrived from Halil on 2026-08-29 and is here now too.
     */
    columns: [
      {
        title: 'Tools',
        links: [
          { label: 'All tools', href: '/en/tools' },
          { label: 'Agentic Readiness Index', href: '/en/tools/agentic-readiness-index' },
          { label: 'Transformation Readiness', href: '/en/tools/transformation-readiness' },
          { label: 'The Guts Check', href: '/en/tools/agentic-guts-check' },
          { label: 'Technology ROI Calculator', href: '/en/tools/roi-calculator' },
        ],
      },
      {
        title: 'Solutions',
        links: [
          { label: 'Strategy Consulting', href: LINKS.strategy },
          { label: 'Transformation Management', href: LINKS.transformation },
          { label: 'Enterprise AI Solutions', href: LINKS.enterpriseAi },
        ],
      },
      {
        title: 'Customers',
        links: [
          { label: 'Hall of Fame', href: LINKS.hallOfFame },
          { label: 'Success Stories', href: LINKS.successStories },
        ],
      },
      {
        title: 'Partners',
        links: [
          { label: 'Partner Program', href: '/en/partner-program' },
        ],
      },
      {
        title: 'Resources',
        links: [
          { label: 'Blog', href: '/en/blog' },
          // The archive, not the sign-up — the subscribe box is two inches to the
          // left of this link in the same footer. A reader who wants to read one
          // before joining had no way in until `/newsletter` shipped.
          { label: 'Newsletter', href: '/en/newsletter' },
          { label: 'Benchmark', href: '/en/impact-labs/benchmark' },
          { label: 'Reports', href: LINKS.reports },
          { label: 'Ebooks', href: '/en/impact-labs/ebooks' },
          { label: 'News', href: '/en/impact-labs/news' },
          { label: 'Podcasts', href: '/en/impact-labs/podcasts' },
          { label: 'Events', href: '/en/impact-labs/events' },
        ],
      },
      {
        title: 'About',
        links: [
          { label: 'About Us', href: LINKS.about },
          { label: 'Careers', href: LINKS.careers },
          { label: 'Contact Us', href: LINKS.contact },
        ],
      },
    ],
    offices: 'New York. London. Istanbul.',
    /**
     * Heading over the social boxes when they are used somewhere they need one
     * — the Contact Us page puts them beside the form, where they are content
     * rather than the last line of the chrome. The footer's own row stays
     * unlabelled: five marks under a copyright line need no introduction.
     */
    followLabel: 'Follow us',
    legal: [
      { label: 'Privacy Policy', href: '/en/privacy-policy' },
      { label: 'Terms of Service', href: '/en/terms-of-service' },
      { label: 'Cookie Notice', href: '/en/cookie-and-data-privacy' },
    ],
    /**
     * The five social accounts, as small icon boxes at the bottom of every page
     * — asked for on 2026-08-26 with the five URLs and the five marks, in this
     * order.
     *
     * **Not per locale, on purpose.** `label` is a brand name, which is the same
     * word in all six languages, and each `url` is one global account — the same
     * reasoning that keeps `stack.ts` English-only. A locale that translated this
     * array would be restating five identical rows and inheriting a broken one
     * the day an account moves.
     *
     * `url` is absolute rather than a `/en/…` path, so `localizeHref()` returns
     * it untouched and `verify-links.mjs` (which walks digitopia.co links only)
     * never tries to fetch it. `icon` is a key in `content/logos.json`, so the
     * box resolves the real mark through the same manifest as the customer wall
     * — the files are 118px square, so `pnpm images` gives them one variant each
     * and the footer reads `fallback`.
     */
    social: [
      {
        label: 'Instagram',
        url: 'https://www.instagram.com/digitopiaglobal/',
        icon: '/logo/instagram.webp',
      },
      {
        label: 'LinkedIn',
        url: 'https://www.linkedin.com/company/digitopiaco/',
        icon: '/logo/linkedin.webp',
      },
      { label: 'YouTube', url: 'https://www.youtube.com/@digitopiaco', icon: '/logo/youtube.webp' },
      {
        label: 'Facebook',
        url: 'https://www.facebook.com/digitopiaglobal',
        icon: '/logo/facebook.webp',
      },
      { label: 'X', url: 'https://x.com/digitopiaco', icon: '/logo/xtwitter.webp' },
    ],
  },

  preview: {
    /**
     * The banner every non-production deployment wears. It is not decoration:
     * preview builds are real public URLs, and the failure mode is somebody
     * quoting a draft line back at us as if it shipped.
     */
    badge: 'Preview',
    notice: 'This is an unapproved preview of digitopia.ai. Content is draft.',
    /** Label on the amber annotations carried from the copy document. */
    checkLabel: 'CHECK',
  },

  /**
   * Words the interface says on its own behalf, with no page behind them —
   * today the two carousel arrows. They are `aria-label`s on icon buttons, so
   * they are never read on screen but are the only thing a screen reader has.
   */
  ui: {
    previous: 'Previous',
    next: 'Next',
  },
}
